import { SiteChangeLog } from "@inrange/building-manager-api-client/models-site";
import { Modal, ModalView } from "@inrange/theme-components";
import { sortBy } from "@inrange/utils";
import { useMemo } from "react";
import { Button, Table } from "react-bootstrap";
import {
  ARRAY_DEDUP_FIELDS,
  filterArrayFieldForDiff,
  formatDetail,
  getUpdatedBy,
  getUserNote,
  removeUnchangedArrayItems,
  timeFromEpochString,
} from "./activityLogHelpers";
import { formatFieldName } from "./ActivityLogMap";

const ActivityDetailRow = ({
  lossFactorsByKey,
  siteId,
  formatFieldName,
  fieldName,
  detail,
}: {
  lossFactorsByKey: Record<string, any>;
  siteId: string;
  formatFieldName: (
    lossFactorsByKey: Record<string, any>,
    fieldName: string
  ) => string;
  fieldName: string;
  detail: { before: any; after: any };
}) => {
  const detailBefore = formatDetail(
    lossFactorsByKey,
    siteId,
    formatFieldName,
    fieldName,
    detail.before
  );
  const detailAfter = formatDetail(
    lossFactorsByKey,
    siteId,
    formatFieldName,
    fieldName,
    detail.after
  );
  return (
    <tr>
      <td style={{ width: "40%" }}>
        {formatFieldName(lossFactorsByKey, fieldName)}
      </td>
      <td style={{ whiteSpace: "pre-wrap" }}>
        {fieldName === "notes" ? "(not shown)" : detailBefore}
      </td>
      <td style={{ whiteSpace: "pre-wrap" }}>{detailAfter}</td>
    </tr>
  );
};

export const ChangelogModal = ({
  logItem,
  siteId,
  lossFactorsByKey,
  onClose,
  zIndex,
}: {
  logItem: SiteChangeLog;
  siteId: string;
  lossFactorsByKey: Record<string, any>;
  onClose: () => void;
  zIndex?: number;
}) => {
  useMemo(() => {
    for (const [fieldName, detail] of Object.entries(logItem.userChange)) {
      if (ARRAY_DEDUP_FIELDS.has(fieldName)) {
        removeUnchangedArrayItems(detail as { before: any[]; after: any[] });
      }
    }
  }, [logItem]);

  const sortByFields = ([a, _]: [string, any]) => {
    return formatFieldName(lossFactorsByKey, a);
  };

  const userChangesLogDetailRows = Object.entries(logItem.userChange)
    .sort(sortBy(sortByFields))
    .map(([fieldName, detail], index) => {
      const filteredDetail = ARRAY_DEDUP_FIELDS.has(fieldName)
        ? filterArrayFieldForDiff(
            fieldName,
            detail as { before: any; after: any }
          )
        : detail;
      return (
        <ActivityDetailRow
          siteId={siteId}
          formatFieldName={formatFieldName}
          fieldName={fieldName}
          detail={filteredDetail}
          key={index}
          lossFactorsByKey={lossFactorsByKey}
        />
      );
    });

  const recalulatedLogDetailRows = Object.entries(logItem.recalculated)
    .sort(sortBy(sortByFields))
    .map(([fieldName, detail], index) => (
      <ActivityDetailRow
        siteId={siteId}
        fieldName={fieldName}
        formatFieldName={formatFieldName}
        detail={detail}
        key={index}
        lossFactorsByKey={lossFactorsByKey}
      />
    ));

  return (
    <Modal>
      <ModalView
        id="activityLogEntry"
        title={
          getUpdatedBy(logItem) + " on " + timeFromEpochString(logItem.time)
        }
        fontWeight="500"
        fontSize="20px"
        zIndex={zIndex}
      >
        <hr />
        {getUserNote(logItem) && (
          <>
            <div>Note: {getUserNote(logItem)}</div>
            <hr />
          </>
        )}
        {userChangesLogDetailRows.length === 0 &&
          recalulatedLogDetailRows.length === 0 && <div>No changes</div>}
        {(userChangesLogDetailRows.length > 0 ||
          recalulatedLogDetailRows.length > 0) && (
          <div
            style={{
              overflow: "auto",
            }}
          >
            <Table
              striped
              bordered
              hover
              style={{ fontSize: "90%", width: "651px" }}
            >
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>{userChangesLogDetailRows}</tbody>
            </Table>
            {recalulatedLogDetailRows &&
              recalulatedLogDetailRows.length > 0 && (
                <>
                  <h6 style={{ fontWeight: "bold" }}>Recalculations</h6>
                  <Table
                    striped
                    bordered
                    hover
                    style={{
                      fontSize: "90%",
                      width: "651px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Value</th>
                        <th>Before</th>
                        <th>After</th>
                      </tr>
                    </thead>
                    <tbody>{recalulatedLogDetailRows}</tbody>
                  </Table>
                </>
              )}
          </div>
        )}

        <hr></hr>
        <div style={{ textAlign: "right" }}>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalView>
    </Modal>
  );
};
